import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _noop from "lodash/noop";
import { IconMagnifyingglass } from "@frameio/vapor-icons/build/esm/icons/IconMagnifyingglass";
import { IconX } from "@frameio/vapor-icons/build/esm/icons/IconX";
import { IconButton } from "../Button";
import { Spinner } from "../Spinner";
import { TextField } from "../TextField";
import { useControlledComponent, useForkedRef, usePrevious } from "../../hooks";
import { useDebouncedChange } from "../../hooks/useDebouncedChange";
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { theme } from "../../theme";
export var SearchField = React.forwardRef(function (_ref, forwardedRef) {
  var _ref$isSearching = _ref.isSearching,
      isSearching = _ref$isSearching === void 0 ? false : _ref$isSearching,
      _ref$debounce = _ref.debounce,
      debounce = _ref$debounce === void 0 ? 500 : _ref$debounce,
      onRequestSearch = _ref.onRequestSearch,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'default' : _ref$variant,
      _ref$defaultQuery = _ref.defaultQuery,
      defaultQuery = _ref$defaultQuery === void 0 ? '' : _ref$defaultQuery,
      _ref$enableSpinner = _ref.enableSpinner,
      enableSpinner = _ref$enableSpinner === void 0 ? true : _ref$enableSpinner,
      _ref$enableClearButto = _ref.enableClearButton,
      enableClearButton = _ref$enableClearButto === void 0 ? true : _ref$enableClearButto,
      _ref$onDebounceStart = _ref.onDebounceStart,
      onDebounceStart = _ref$onDebounceStart === void 0 ? _noop : _ref$onDebounceStart,
      _ref$onDebounceEnd = _ref.onDebounceEnd,
      onDebounceEnd = _ref$onDebounceEnd === void 0 ? _noop : _ref$onDebounceEnd,
      _ref$onQueryChange = _ref.onQueryChange,
      onQueryChange = _ref$onQueryChange === void 0 ? _noop : _ref$onQueryChange,
      _ref$clearLabel = _ref.clearLabel,
      clearLabel = _ref$clearLabel === void 0 ? 'Clear input value' : _ref$clearLabel,
      query = _ref.query,
      other = _objectWithoutProperties(_ref, ["isSearching", "debounce", "onRequestSearch", "variant", "defaultQuery", "enableSpinner", "enableClearButton", "onDebounceStart", "onDebounceEnd", "onQueryChange", "clearLabel", "query"]);

  var inputRef = useRef(null);
  var ref = useForkedRef(forwardedRef, inputRef);

  var _useControlledCompone = useControlledComponent(query, onQueryChange, defaultQuery),
      _useControlledCompone2 = _slicedToArray(_useControlledCompone, 2),
      value = _useControlledCompone2[0],
      setValue = _useControlledCompone2[1];

  var _useDebouncedChange = useDebouncedChange(onRequestSearch, debounce),
      onChange = _useDebouncedChange.onChange,
      isDebounced = _useDebouncedChange.isDebounced;

  var showSpinner = (isDebounced || isSearching) && enableSpinner;
  var showClear = value && !showSpinner;
  var prevDebounced = usePrevious(isDebounced);
  useEffect(function () {
    if (prevDebounced !== isDebounced) {
      if (isDebounced) {
        onDebounceStart();
      } else {
        onDebounceEnd();
      }
    }
  }, [isDebounced, onDebounceEnd, onDebounceStart, prevDebounced]);

  function handleInput(v) {
    setValue(v);
    onQueryChange(v);
    onChange(v);
  }

  return /*#__PURE__*/React.createElement(StyledTextField, _extends({
    $variant: variant,
    adornmentBefore: /*#__PURE__*/React.createElement(IconMagnifyingglass, null),
    adornmentAfter: /*#__PURE__*/React.createElement(AdornmentContainer, null, enableSpinner && /*#__PURE__*/React.createElement(Spinner, {
      centered: true,
      delay: 0,
      isLoading: isDebounced || isSearching,
      size: "x-small"
    }), enableClearButton && /*#__PURE__*/React.createElement(FadeIn, {
      "aria-hidden": !showClear,
      $visible: showClear,
      $hasValue: !!value
    }, /*#__PURE__*/React.createElement(IconButton, {
      variant: "link",
      label: clearLabel,
      size: "small",
      icon: /*#__PURE__*/React.createElement(IconX, {
        size: 16
      }),
      onClick: function onClick() {
        handleInput('');
        inputRef.current.focus();
      }
    }))),
    type: "search",
    onChange: function onChange(e) {
      var value = e.target.value;
      handleInput(value);
    },
    ref: ref,
    value: value
  }, other));
});
var StyledTextField = styled(TextField).withConfig({
  displayName: "SearchField__StyledTextField",
  componentId: "vapor__sc-1kdhqd9-0"
})(["", ""], function (p) {
  return p.$variant === 'pill' && "\n    border-radius: ".concat(theme.spaces[32], ";\n  ");
});
var AdornmentContainer = styled.div.withConfig({
  displayName: "SearchField__AdornmentContainer",
  componentId: "vapor__sc-1kdhqd9-1"
})(["width:", ";display:flex;justify-content:center;position:relative;"], theme.spaces[24]);
var FadeIn = styled.div.withConfig({
  displayName: "SearchField__FadeIn",
  componentId: "vapor__sc-1kdhqd9-2"
})(["position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);transition-timing-function:linear;transition-property:opacity,visibility;transition-duration:", ";", ";"], function (p) {
  return p.$hasValue ? theme.transitions.durations[100] : '50ms';
}, function (p) {
  return !p.$visible ? "\n    opacity: 0;\n    pointer-events: none;\n    transition-delay: 0;\n    visibility: hidden;\n  " : "\n    opacity: 1;\n    pointer-events: auto;\n    visibility: visible;\n  ";
});